import { UI_TEXT, isPlainObject } from './defaults'
import type { FormDefinition, FormError } from './types'

export type TrackingPayload = {
  fbc?: string
  fbp?: string
  fbclid?: string
  gclid?: string
  gbraid?: string
  wbraid?: string
  gaClientId?: string
  pageUrl: string
  referrer?: string
  utm: Record<string, string>
}

export type CreateSubmissionBody = {
  values: Record<string, string>
  tracking: TrackingPayload
  overrides?: { pipeId?: number; stageId?: number }
  landingId?: number
  lgpdAccepted?: boolean
  hp?: string
  elapsedMs: number
}

export type UpdateSubmissionBody = Omit<CreateSubmissionBody, 'overrides' | 'landingId'>

export type PageviewBody = {
  eventId: string
  pageUrl?: string
  referrer?: string
  fbc?: string
  fbp?: string
}

export class ApiError extends Error {
  readonly detail: FormError
  readonly status: number

  constructor(detail: FormError, status: number) {
    super(detail.message)
    this.detail = detail
    this.status = status
  }
}

const REQUEST_TIMEOUT_MS = 20000

function codeFromStatus(status: number): string {
  if (status === 404) return 'form_unavailable'
  if (status === 429) return 'rate_limited'
  if (status === 401) return 'invalid_token'
  if (status === 403) return 'forbidden_origin'
  if (status === 409) return 'submission_pending'
  if (status === 422) return 'validation'
  return 'unknown'
}

async function readError(response: Response): Promise<FormError> {
  const fallback: FormError = { code: codeFromStatus(response.status), message: UI_TEXT.genericError }
  try {
    const body: unknown = await response.json()
    const error = isPlainObject(body) ? body.error : undefined
    if (!isPlainObject(error)) return fallback
    const detail: FormError = {
      code: typeof error.code === 'string' ? error.code : fallback.code,
      message: typeof error.message === 'string' && error.message ? error.message : fallback.message,
    }
    if (typeof error.field === 'string') detail.field = error.field
    return detail
  } catch {
    return fallback
  }
}

export class Api {
  private readonly base: string
  private readonly controllers = new Set<AbortController>()

  constructor(base: string) {
    this.base = base.replace(/\/+$/, '')
  }

  abortAll(): void {
    this.controllers.forEach((controller) => controller.abort())
    this.controllers.clear()
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const controller = new AbortController()
    this.controllers.add(controller)
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    let response: Response
    try {
      response = await fetch(this.base + path, { ...init, credentials: 'omit', signal: controller.signal })
    } catch {
      throw new ApiError({ code: 'network', message: UI_TEXT.networkError }, 0)
    } finally {
      clearTimeout(timer)
      this.controllers.delete(controller)
    }
    if (!response.ok) throw new ApiError(await readError(response), response.status)
    const body: unknown = await response.json().catch(() => null)
    if (!isPlainObject(body) || !('data' in body)) {
      throw new ApiError({ code: 'unknown', message: UI_TEXT.genericError }, response.status)
    }
    return body.data as T
  }

  getDefinition(publicId: string): Promise<FormDefinition> {
    return this.request<FormDefinition>(`/public/forms/${encodeURIComponent(publicId)}`)
  }

  createSubmission(publicId: string, body: CreateSubmissionBody): Promise<{ submissionToken: string; eventId: string }> {
    return this.request(`/public/forms/${encodeURIComponent(publicId)}/submissions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
  }

  updateSubmission(publicId: string, token: string, body: UpdateSubmissionBody): Promise<{ eventId: string }> {
    return this.request(`/public/forms/${encodeURIComponent(publicId)}/submissions`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Submission-Token': token },
      body: JSON.stringify(body),
    })
  }

  /**
   * Fora do `request` de propósito: é aviso, não pedido. Vai com `keepalive` para sobreviver a quem
   * sai da página no mesmo instante, e engole qualquer falha — PageView perdido não pode derrubar
   * o formulário, que é o motivo de a página existir.
   */
  sendPageview(publicId: string, body: PageviewBody): void {
    try {
      void fetch(`${this.base}/public/forms/${encodeURIComponent(publicId)}/pageview`, {
        method: 'POST',
        credentials: 'omit',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).catch(() => undefined)
    } catch {
      // rede indisponível não é problema do formulário
    }
  }

  getCities(uf: string): Promise<string[]> {
    return this.request<string[]>(`/public/geo/cities/${encodeURIComponent(uf)}`)
  }
}
